// Admin > Settings > How To. Plain-English walkthrough of the EVRST Studio
// Prints admin, sequenced like a real order: customer designs -> places
// order -> admin approves artwork -> production prints/presses/QCs/packs ->
// admin marks shipped. Each section names the exact admin page it covers
// and links straight to it so a new admin can click their way through the
// flow without guessing.
import { Link } from 'react-router-dom'
import { useState } from 'react'

interface Step {
  num: number
  title: string
  who: 'customer' | 'admin' | 'production'
  page?: { label: string; to: string }
  body: React.ReactNode
}

const STEPS: Step[] = [
  {
    num: 1, who: 'admin',
    title: 'Set up garments + DTF products',
    page: { label: 'Admin -> Catalog -> Garments', to: '/admin/garments' },
    body: (
      <>
        <p>Before a customer can design anything, every garment (tee, polo, hoodie, hat, long sleeve) and the DTF transfer SKU need to exist in the catalog with a mockup photo and print locations defined.</p>
        <ul>
          <li><strong>Mockup image</strong> - the flat photo of the blank garment shown behind the Designer canvas.</li>
          <li><strong>Print locations</strong> - Front, Back, Left Sleeve, Right Sleeve, etc. Drag the yellow box to set where on the garment that print can land. Front is the only one shown to customers by default; sleeves and back live behind a "+ Add print location" tray until the customer opts in.</li>
          <li><strong>Variants</strong> (sizes/colours) - drive the size matrix on the order side.</li>
        </ul>
        <p><em>Tip:</em> use the same upload-style mockup for every garment in a family (the renderer scales it consistently). Save before walking away - changes only persist on click.</p>
      </>
    ),
  },
  {
    num: 2, who: 'admin',
    title: 'Stock the Media Library with reusable artwork',
    page: { label: 'Admin -> Catalog -> Media Library', to: '/admin/media' },
    body: (
      <>
        <p>Curated artwork the studio offers customers from inside the Designer's media picker. Organise into folders (Logos, Hero imagery, Patterns, etc.) and search by filename. Files dropped into the library are immediately available to every signed-in or guest builder session.</p>
        <ul>
          <li>Click <strong>+ Upload artwork</strong> (multi-select works) - new files land in whichever folder is currently selected.</li>
          <li>Click <strong>+ New</strong> next to FOLDERS to create one. Each folder shows its live file count.</li>
          <li>Hover a file -&gt; the <strong>arrows icon</strong> opens a "Move to" menu. <strong>x</strong> soft-deletes (the file stays on disk so any past design that referenced it still works).</li>
          <li>Hover a folder -&gt; <strong>pencil</strong> rename or <strong>x</strong> delete (files inside drop back to Unfiled).</li>
        </ul>
      </>
    ),
  },
  {
    num: 3, who: 'customer',
    title: 'Customer designs their garment',
    page: { label: 'Customer Designer', to: '/designer' },
    body: (
      <>
        <p>The public Designer is where the customer assembles their print. They pick a product, choose a colour and sizes, then drop artwork onto the canvas (uploaded, from the Media Library, or AI-generated).</p>
        <ul>
          <li><strong>Front</strong> is the only print location shown by default. The customer clicks <strong>+ Add print location</strong> in the sidebar to reveal Back / Left Sleeve / Right Sleeve / etc.</li>
          <li>Every uploaded artwork gets a live DPI check against the placement box - flagged on press if it falls below 200 DPI.</li>
          <li><strong>Save</strong> persists a draft to the customer's library. Adding to cart pins a price snapshot to the design.</li>
        </ul>
      </>
    ),
  },
  {
    num: 4, who: 'customer',
    title: 'Customer pays + the order lands',
    page: { label: 'Admin -> Processing -> Orders', to: '/admin/orders' },
    body: (
      <>
        <p>Checkout captures payment via Stripe and creates an order with the design + shipping snapshot attached. Newly paid orders show up at the top of the Orders list with status <strong>Paid</strong>.</p>
        <ul>
          <li><strong>Click the order number OR the customer name</strong> in the list to open the detail page - both are clickable.</li>
          <li>Status filter chips at the top let you narrow to a single bucket (Pending payment, Paid, In production, Shipped, etc.). Search by order #, email, or guest email.</li>
        </ul>
        <p>If a customer abandoned mid-design without paying, the draft lands in <Link to="/admin/abandoned-carts" className="text-gold-dark hover:underline">Abandoned carts</Link> after one hour of inactivity.</p>
      </>
    ),
  },
  {
    num: 5, who: 'admin',
    title: 'Review the artwork proof',
    page: { label: 'Admin -> Production -> Artwork Approval', to: '/admin/artwork-approval' },
    body: (
      <>
        <p>Every paid order ships its design proof to the Artwork Approval queue. Tabs at the top split the queue into <strong>Pending</strong>, <strong>Approved</strong>, <strong>Revision requested</strong>, and <strong>Rejected</strong>.</p>
        <ul>
          <li><strong>Approve</strong> -&gt; the order is now eligible to enter the production floor.</li>
          <li><strong>Revision</strong> -&gt; sends the design back to the customer with a note. The note is mandatory; the button stays disabled until you type one (the customer needs to know what to change).</li>
          <li><strong>Reject</strong> -&gt; blocks the design. Add a note explaining why so the customer can resupply.</li>
          <li>Mis-clicked? Every card in a non-Pending tab has a small <strong>"Move back to pending"</strong> link at the bottom-right.</li>
        </ul>
      </>
    ),
  },
  {
    num: 6, who: 'admin',
    title: 'Open the order to see production artwork',
    page: { label: 'Order detail page', to: '/admin/orders' },
    body: (
      <>
        <p>Once the artwork is approved, opening the order reveals the <strong>Production Artwork</strong> section. This is the part production uses, not the customer-facing proof.</p>
        <ul>
          <li><strong>Garments</strong> show each placement (Front centre, Back full, Chest left, Right sleeve, etc.) as its own card with the print dimensions in mm, the file's native px size, and a colour-coded DPI badge.</li>
          <li><strong>DTF orders</strong> show each gang sheet, then every transfer on it with its quantity.</li>
          <li><strong>Quality grades</strong>:
            <span className="inline-flex flex-wrap gap-1.5 mt-1">
              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded bg-emerald-100 text-emerald-800 border border-emerald-200">Excellent 300+ DPI</span>
              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Good 250-299</span>
              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded bg-amber-50 text-amber-800 border border-amber-200">Acceptable 200-249</span>
              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded bg-orange-50 text-orange-800 border border-orange-200">Low 150-199</span>
              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded bg-red-50 text-red-800 border border-red-200">Reject &lt;150</span>
            </span>
          </li>
          <li>Click <strong>Download for press</strong> to grab the original PNG at full resolution. <strong>Preview</strong> opens it full-size in a new tab.</li>
        </ul>
      </>
    ),
  },
  {
    num: 7, who: 'production',
    title: 'Track the order through the production stages',
    page: { label: 'Admin -> Production -> Production Queue', to: '/admin/production' },
    body: (
      <>
        <p>Once paid + artwork-approved, an order shows up on the Production Queue. The tab strip splits the queue into stages so production can pick up exactly where the previous shift left off:</p>
        <ul>
          <li><strong>Not started</strong> - paid, artwork approved, but no stage logged yet.</li>
          <li><strong>In Print</strong> - on the press right now.</li>
          <li><strong>Pressed</strong> - off the press, awaiting QC.</li>
          <li><strong>QC</strong> - quality check in progress.</li>
          <li><strong>Packed</strong> - ready to ship.</li>
        </ul>
        <p>Click a stage button on each order card to log that event. Every click writes a timeline entry visible on the order's detail page (with the staff member's name + timestamp) so the full chain of custody is auditable later.</p>
      </>
    ),
  },
  {
    num: 8, who: 'admin',
    title: 'Pack + ship',
    page: { label: 'Admin -> Production -> Shipping', to: '/admin/shipping' },
    body: (
      <>
        <p>When the order hits Packed, drop into the Shipping page to enter the carrier and tracking number, then mark the order <strong>Shipped</strong>. The customer is notified via email automatically.</p>
        <ul>
          <li>The shipping snapshot on every order locks in the address that was current at checkout - even if the customer later edits their profile, what gets printed on the label is what they paid with.</li>
        </ul>
      </>
    ),
  },
  {
    num: 9, who: 'admin',
    title: 'Watch the dashboards',
    page: { label: 'Admin -> Production -> Overview', to: '/admin' },
    body: (
      <>
        <p>Two dashboards summarise everything moving through the studio. Each carries a date-range toolbar at the top with preset chips (Today, 7 days, 30 days, This month, Last month, YTD, All time) plus a custom From/To. Every tile and chart narrows to the selected range.</p>
        <ul>
          <li><Link to="/admin" className="text-gold-dark hover:underline"><strong>Overview</strong></Link> - tiles for Pending payment, Awaiting artwork, In production, Shipped, plus Top products sold and Top states (by ship-to). Open this first thing in the morning.</li>
          <li><Link to="/admin/processing/dashboard" className="text-gold-dark hover:underline"><strong>Processing Dashboard</strong></Link> - the deeper view. Orders/revenue timeline, payments captured vs refunded, pending-payment aging, quote pipeline, abandoned designs, top customers.</li>
        </ul>
      </>
    ),
  },
  {
    num: 10, who: 'admin',
    title: 'Edge cases + housekeeping',
    body: (
      <>
        <ul>
          <li><strong>Refund</strong> - the Order detail page has a "Refund via Stripe" button when the order carries a Stripe payment reference. Full refund only from the UI right now; partial via Stripe dashboard.</li>
          <li><strong>Cancel</strong> - any non-shipped order can be cancelled from the status menu on its detail page. The action is logged in the production timeline.</li>
          <li><strong>Stale unpaid orders</strong> - <Link to="/admin/orders?status=pending_payment" className="text-gold-dark hover:underline">Pending payment</Link> can be filtered + the Processing Dashboard's "Pending payment aging" widget surfaces anything sitting 7-30d or 30d+.</li>
          <li><strong>Users</strong> - <Link to="/admin/settings/users" className="text-gold-dark hover:underline">Settings -&gt; Users</Link> manages admin/manager/production access. Customers self-register via the public sign-in.</li>
          <li><strong>Stripe + email</strong> - <Link to="/admin/settings/stripe" className="text-gold-dark hover:underline">Settings -&gt; Stripe</Link> for the keys + webhook. Email is configured at the platform level (Resend).</li>
        </ul>
      </>
    ),
  },
]

const WHO_STYLE: Record<Step['who'], { label: string; cls: string }> = {
  customer:   { label: 'Customer',   cls: 'bg-blue-100 text-blue-800     border border-blue-200' },
  admin:      { label: 'Admin',      cls: 'bg-gold/15 text-gold-dark     border border-gold/30' },
  production: { label: 'Production', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
}

export default function SettingsHowTo() {
  const [open, setOpen] = useState<Set<number>>(new Set(STEPS.map((s) => s.num)))

  function toggle(num: number) {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(num)) next.delete(num); else next.add(num)
      return next
    })
  }
  function expandAll()   { setOpen(new Set(STEPS.map((s) => s.num))) }
  function collapseAll() { setOpen(new Set()) }

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Settings</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">How To</h1>
          <p className="text-ink-2 text-sm mt-2 max-w-2xl">A start-to-finish walkthrough of EVRST Studio Prints, in the order a real order flows through the system. Click any step to fold/unfold; each section links straight to the page it talks about.</p>
        </div>
        <div className="flex gap-2 whitespace-nowrap">
          <button onClick={expandAll} className="text-[11px] font-semibold uppercase tracking-wide text-ink-2 hover:text-ink underline-offset-2 hover:underline">Expand all</button>
          <span className="text-ink-3 text-[11px]">·</span>
          <button onClick={collapseAll} className="text-[11px] font-semibold uppercase tracking-wide text-ink-2 hover:text-ink underline-offset-2 hover:underline">Collapse all</button>
        </div>
      </div>

      <div className="space-y-3">
        {STEPS.map((s) => {
          const isOpen = open.has(s.num)
          const who = WHO_STYLE[s.who]
          return (
            <div key={s.num} className="card !p-0 overflow-hidden">
              <button onClick={() => toggle(s.num)}
                className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-surface transition-colors">
                <span className="w-7 h-7 rounded-full bg-ink text-white font-bold text-xs flex items-center justify-center shrink-0">{s.num}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-ink font-semibold text-base truncate">{s.title}</div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={'inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded ' + who.cls}>{who.label}</span>
                    {s.page && <span className="text-[11px] text-ink-3">{s.page.label}</span>}
                  </div>
                </div>
                <span className={'text-ink-3 text-lg transition-transform shrink-0 ' + (isOpen ? 'rotate-90' : '')} aria-hidden>›</span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 border-t border-border bg-white">
                  <div className="prose prose-sm max-w-none text-ink-2 [&_strong]:text-ink [&_p]:mt-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-2 [&_li]:mb-1.5 [&_em]:text-ink-3">
                    {s.body}
                  </div>
                  {s.page && (
                    <div className="mt-4">
                      <Link to={s.page.to} className="btn-outline text-xs">Open {s.page.label} -&gt;</Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="card mt-8">
        <div className="eyebrow">Reference</div>
        <p className="text-sm text-ink-2 mt-1">Need a quicker map of the admin? Every page in the left sidebar is grouped: <strong>Production</strong> for the daily floor, <strong>Processing</strong> for orders/quotes/payments, <strong>Catalog</strong> for products/media, <strong>Business</strong> for customers/companies/statements, and <strong>Settings</strong> for platform configuration and this page.</p>
      </div>
    </div>
  )
}
